// Guest Database — unique guests aggregated from all reservations.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Search, Mail, Phone } from "lucide-react";
import { adminApi } from "@/lib/adminAuth";

const usd = (n) => `$${Number(n || 0).toFixed(0)}`;

export default function AdminGuests() {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get(`/admin/guests${q ? `?q=${encodeURIComponent(q)}` : ""}`);
            setRows(data);
        } catch { toast.error("Could not load guests"); }
        finally { setLoading(false); }
    };

    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

    return (
        <div data-testid="admin-guests-page">
            <header className="mb-6">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Contacts</div>
                <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Guest database</h1>
                <p className="text-[#0b1f22]/60 mt-2 text-sm">Every guest who has ever booked, grouped by email. Click a row to open their most recent reservation.</p>
            </header>

            <div className="mb-4 relative max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
                <input
                    data-testid="guest-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name, email or phone"
                    className="w-full pl-9 pr-3 py-2.5 rounded-full border border-navy/15 text-sm focus:border-gold focus:outline-none bg-white"
                />
            </div>

            {loading ? <div className="text-navy/60 text-sm">Loading guests…</div> : (
                <div className="bg-white rounded-2xl border border-navy/10 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-navy/5 text-[10px] uppercase tracking-[0.18em] text-navy/60">
                            <tr>
                                <th className="text-left px-5 py-3">Guest</th>
                                <th className="text-left px-5 py-3 hidden md:table-cell">Contact</th>
                                <th className="text-left px-5 py-3">Stays</th>
                                <th className="text-left px-5 py-3">Total spent</th>
                                <th className="text-left px-5 py-3 hidden md:table-cell">Last stay</th>
                                <th className="text-right px-5 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((g) => (
                                <tr key={g.email} data-testid={`guest-row-${g.email}`} className="border-t border-navy/10">
                                    <td className="px-5 py-4">
                                        <div className="font-medium text-navy">{g.guest_name || "—"}</div>
                                        <div className="text-[11px] text-navy/50">{g.country || ""}</div>
                                    </td>
                                    <td className="px-5 py-4 hidden md:table-cell text-navy/70">
                                        <div className="flex items-center gap-1.5 text-[12px]"><Mail size={11} className="text-gold" /> {g.email}</div>
                                        {g.phone && <div className="flex items-center gap-1.5 text-[12px] mt-1"><Phone size={11} className="text-gold" /> {g.phone}</div>}
                                    </td>
                                    <td className="px-5 py-4 text-navy font-medium">{g.stays}</td>
                                    <td className="px-5 py-4 text-navy font-display">{usd(g.total_spent)}</td>
                                    <td className="px-5 py-4 text-navy/70 hidden md:table-cell">{g.last_stay || "—"}</td>
                                    <td className="px-5 py-4 text-right">
                                        {g.latest_reservation_id ? (
                                            <Link to={`/admin/reservations/${g.latest_reservation_id}`} className="text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full bg-navy text-ivory">Open</Link>
                                        ) : <span className="text-navy/30 text-xs">—</span>}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-10 text-center text-navy/50">
                                    <Users size={20} className="mx-auto mb-2 text-navy/30" /> No guests found
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
